// @flow

import { array, boolean, number, object, string } from 'yup';
import { FORM_NAMES } from '@streaming-projects/enums';

import {
  areAllValuesInGivenObjectNonNull,
  getAllElementsOfArrayExceptLast,
  getValuesOfGivenKeysFromObjectAsArray,
  groupArrayOfObjectsByGivenKey,
} from '../../common-utils/utils';
import { MAX_INPUT_VALUE_FOR_ANY_FIELD } from '../../constants';
import { doDimensionValuesConflict } from '../custom-discounts/utils';
import { formatCurrency0DP } from '../views/grid-utils/renderers';

import { ESTIMATE_CC_METRIC_NAMES, ESTIMATE_COMMITTED_CAPACITY } from './enums';
import {
  getDimensionLevelConfigArrayFromMetricRowForEstimateCC,
  getIfGivenRowIsANewlyAddedRowForEstimateCC,
  getIfGivenRowIsBeingEditedORANewOneForEstimateCC,
  getMetricConfigBasedOnMetricNameForEstimateCC,
  getMetricDimensionsFromMetricNameForEstimateCC,
  getDiscountBoundsCheckForEstimateCC,
  getMaxQuantityForMetricAndUserTypeForEstimateCC,
  getMinQuantityForMetricAndUserTypeForEstimateCC,
  getRowIdOfGivenRowForEstimateCC,
} from './utils';

const conflictsTest = (data, { createError, path, from }) => {
  const currRow = from[0].value;
  // Only check for conflicts on row addition
  if (!getIfGivenRowIsANewlyAddedRowForEstimateCC(currRow)) {
    return true;
  }

  const parentInputs = from[1].value;
  const metricName = parentInputs[ESTIMATE_COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME];
  const allRows =
    parentInputs[ESTIMATE_COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY];

  const dimRelatedKeys = getMetricDimensionsFromMetricNameForEstimateCC(metricName);
  const inProgressRowDimValues = getValuesOfGivenKeysFromObjectAsArray(currRow, dimRelatedKeys);

  let conflictingRowNum = null;

  // If there are more than 1 Committed Capacity Config items defined and all the values are filled
  if (allRows.length > 1 && areAllValuesInGivenObjectNonNull(currRow)) {
    // Then check if the last row value conflicts with any other row
    for (let rowNumber = 0; rowNumber <= allRows.length - 2; rowNumber++) {
      const existingRowDimValues = getValuesOfGivenKeysFromObjectAsArray(
        allRows[rowNumber],
        dimRelatedKeys
      );

      if (doDimensionValuesConflict(inProgressRowDimValues, existingRowDimValues)) {
        conflictingRowNum = rowNumber;
        break;
      }
    }
  }

  if (conflictingRowNum != null) {
    return createError({
      message: `This row conflicts with row ${conflictingRowNum + 1} , please check!`,
      path: path,
    });
  }

  return true;
};

const formatCommittedCapacityIntoFrontEndFormat = (committedCapacityInputs) => {
  // Step 1: Group the array by metric_type
  const groupedByMetric = groupArrayOfObjectsByGivenKey(committedCapacityInputs, 'metric_type');

  // Step 2: Transform each metric group
  return Object.entries(groupedByMetric).map(([metricType, items]) => ({
    metricName: metricType, // FE uses metricName everywhere which is more natural way of thinking about it
    [ESTIMATE_COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY]: items.map(
      (item) => ({
        [ESTIMATE_COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME]: getRowIdOfGivenRowForEstimateCC(item),
        [ESTIMATE_COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME]:
          item[ESTIMATE_COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME],
        [ESTIMATE_COMMITTED_CAPACITY.DISCOUNT_BACKEND_FIELD_NAME]:
          item[ESTIMATE_COMMITTED_CAPACITY.DISCOUNT_BACKEND_FIELD_NAME],
        [ESTIMATE_COMMITTED_CAPACITY.IS_DENSER_BIN_PACKING_BACKEND_FIELD_NAME]:
          item[ESTIMATE_COMMITTED_CAPACITY.IS_DENSER_BIN_PACKING_BACKEND_FIELD_NAME] ?? false,
        [ESTIMATE_COMMITTED_CAPACITY.IS_ADMIN_EDITED_BACKEND_FIELD_NAME]:
          item[ESTIMATE_COMMITTED_CAPACITY.IS_ADMIN_EDITED_BACKEND_FIELD_NAME] ?? false,
        ...item.resource_configuration_type,
      })
    ),
  }));
};

export const committedCapacityValidationFunc = (estimate) => {
  const { isUserAdmin } = estimate;
  return array(
    object({
      [ESTIMATE_COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME]: string()
        .required()
        .label(ESTIMATE_COMMITTED_CAPACITY.METRIC_NAME_DISPLAY_NAME)
        .nullable()
        .typeError('Please select a metric!')
        .test(
          'onlyOneRowCanBeAddedAtATime',
          'Please ensure only 1 row is added at a time',
          function () {
            const dimensionLevelRows = getDimensionLevelConfigArrayFromMetricRowForEstimateCC(
              this.parent
            );
            return getAllElementsOfArrayExceptLast(dimensionLevelRows).every(
              (dimensionConfigItem) =>
                !getIfGivenRowIsANewlyAddedRowForEstimateCC(dimensionConfigItem)
            );
          }
        )
        .test(
          'atLeastOneDimRowShouldBeDefined',
          'Please ensure at least 1 row is added inside a metric',
          function () {
            const dimensionLevelRows = getDimensionLevelConfigArrayFromMetricRowForEstimateCC(
              this.parent
            );
            return dimensionLevelRows.length > 0;
          }
        ),
      [ESTIMATE_COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY]: array(
        object({
          [ESTIMATE_COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME]: number()
            .nullable()
            .label(ESTIMATE_COMMITTED_CAPACITY.QUANTITY_DISPLAY_NAME)
            // DSP Starter Pack repurposes this shared 'quantity' field to hold a commitment
            // dollar amount, with its own required/range rules. This test must stay ahead of
            // .integer()/.min()/.max() below: Formik only keeps the FIRST error per field path,
            // so declaring it first is what makes the "Commitment Amount ..." wording win for
            // DSP instead of the generic "Quantity ..." messages those produce.
            .test('quantityValidation', (value, { createError, path, from }) => {
              const parentInputs = from[1].value;
              const metricName =
                parentInputs[ESTIMATE_COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME];

              if (metricName === ESTIMATE_CC_METRIC_NAMES.DSP_STARTER_PACK) {
                const currRow = from[0].value;
                if (!getIfGivenRowIsBeingEditedORANewOneForEstimateCC(currRow)) {
                  return true;
                }

                if (value == null) {
                  return createError({
                    message: 'Commitment Amount is a required field!',
                    path: path,
                  });
                }

                if (value < 0) {
                  return createError({
                    message: 'Commitment Amount must be greater than or equal to 0!',
                    path: path,
                  });
                }

                if (!Number.isInteger(value)) {
                  return createError({
                    message: 'Commitment Amount must be a whole number!',
                    path: path,
                  });
                }

                // Non-admins are capped (see maxQuantityForNonAdmins); admins get the global max.
                const maxCommitmentAmount = getMaxQuantityForMetricAndUserTypeForEstimateCC(
                  metricName,
                  isUserAdmin
                );
                if (value > maxCommitmentAmount) {
                  return createError({
                    message: `Commitment Amount must be less than or equal to ${formatCurrency0DP.format(
                      maxCommitmentAmount
                    )}!`,
                    path: path,
                  });
                }

                return true;
              }

              if (value == null) {
                return createError({
                  message: 'Quantity is a required field!',
                  path: path,
                });
              }

              return true;
            })
            .integer()
            .typeError('Quantity is a required field!')
            .min(0)
            .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
            .test('conflictsExist', 'Please ensure there are NO conflicts', conflictsTest)
            .test('dynamicMinQuantityCheck', (value, { createError, path, from }) => {
              const currRow = from[0].value;

              /*
              Only check min when the user is editing the row or configuring for the first time.Why?
              Think of this scenario:
              1. Admin User Configures Quantity as say 2 for ECKU. This is allowed for an admin but not for a non-admin.
              2. For a non-admin, this should not result in any error UNTIL he touches that field
            */

              if (!getIfGivenRowIsBeingEditedORANewOneForEstimateCC(currRow)) {
                return true;
              }

              const parentInputs = from[1].value;
              const metricName =
                parentInputs[ESTIMATE_COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME];
              const minQuantity = getMinQuantityForMetricAndUserTypeForEstimateCC(
                metricName,
                isUserAdmin
              );

              if (value < minQuantity) {
                return createError({
                  message: `Quantity should be greater than or equal to ${minQuantity}!`,
                  path: path,
                });
              }

              return true;
            }),
          [ESTIMATE_COMMITTED_CAPACITY.IS_DENSER_BIN_PACKING_BACKEND_FIELD_NAME]: boolean()
            .nullable()
            .default(false),
          [ESTIMATE_COMMITTED_CAPACITY.DISCOUNT_BACKEND_FIELD_NAME]: number()
            .nullable()
            .typeError('Discount must be a number!')
            .label(ESTIMATE_COMMITTED_CAPACITY.DISCOUNT_DISPLAY_NAME)
            .test('discountValidation', (value, { createError, path, from }) => {
              const parentInputs = from[1].value;
              const metricName =
                parentInputs[ESTIMATE_COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME];

              const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
              if (!metricConfig.shouldShowDiscountField) {
                return true;
              }

              const currRow = from[0].value;
              if (!getIfGivenRowIsBeingEditedORANewOneForEstimateCC(currRow)) {
                return true;
              }

              if (value == null) {
                return createError({
                  message: '% Discount is a required field!',
                  path: path,
                });
              }

              if (value < 0 || value > 100) {
                return createError({
                  message: 'Discount must be between 0 and 100!',
                  path: path,
                });
              }

              // 100% discount divides by zero in calculateListPrice's list-price formula
              // (discountedPrice / (1 - discount / 100)) — invalid for DSP Starter Pack.
              if (metricName === ESTIMATE_CC_METRIC_NAMES.DSP_STARTER_PACK && value >= 100) {
                return createError({
                  message: 'Discount must be less than 100 for Streamhouse Accelerator!',
                  path: path,
                });
              }

              if (!isUserAdmin) {
                const { minDiscount, maxDiscount, isOutOfBounds } =
                  getDiscountBoundsCheckForEstimateCC(metricName, value, estimate, currRow);

                if (isOutOfBounds) {
                  return createError({
                    message: `Discount must be between ${minDiscount} and ${maxDiscount}!`,
                    path: path,
                  });
                }
              }

              return true;
            }),
        })
      ).min(1),
    })
  );
};

export const COMMITTED_CAPACITY_JSON_CONFIG = {
  backendFieldName: ESTIMATE_COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY,
  inputTransformationFunc: formatCommittedCapacityIntoFrontEndFormat,
  validation: committedCapacityValidationFunc,
  belongingForm: FORM_NAMES.COMMITTED_CAPACITY.COMMITTED_CAPACITY_FORM,
};
